import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Event,
  GetWaitlistsByEventCommandOutput,
  Shift,
  WaitlistEntry,
} from '@amzn/red-velvet-api';
import { useFeatures, getEnabledFeatures } from '../features';
import { getClient } from '../../utils/getClient';

const DEFAULT_PAGE_SIZE = 100;

export interface WaitlistsByEvent {
  event?: Event;
  shifts: Shift[];
  waitlistEntries: WaitlistEntry[];
}

/**
 * Loads everything the coordinator Manage Waitlist page needs from a single API call chain:
 * GetWaitlistsByEvent returns the event and ALL of its shifts on every page (identical across pages),
 * plus one page of waitlist entries. We drain every page in a single query, capturing event/shifts
 * once and concatenating all entries. Pagination/filtering then happen client-side.
 *
 * @param eventId  the event whose waitlist to load
 * @param pageSize entries requested per page (defaults to 100)
 */
export function useGetAllWaitlistsByEvent(eventId: string, pageSize: number = DEFAULT_PAGE_SIZE) {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const featureFlags = getEnabledFeatures(features, searchParams);
  return useQuery<WaitlistsByEvent>({
    queryKey: ['RedVelvetApi', 'getWaitlistsByEvent', { eventId, pageSize }],
    enabled: !!eventId,
    queryFn: async () => {
      const client = getClient(featureFlags);
      let page: string | undefined = undefined;
      let event: Event | undefined;
      let shifts: Shift[] = [];
      const waitlistEntries: WaitlistEntry[] = [];

      do {
        const response: GetWaitlistsByEventCommandOutput = await client.getWaitlistsByEvent({
          eventId,
          pageSize,
          page,
        });
        // event and shifts are the same on every page — capture them from the first response only.
        if (!event) {
          event = response.event;
          shifts = response.shifts ?? [];
        }
        waitlistEntries.push(...(response.waitlistEntries ?? []));
        page = response.page;
      } while (page);

      return { event, shifts, waitlistEntries };
    },
  });
}
